import { FormGroup } from '@angular/forms';
import { PolygonMapItem } from './polygon-map-item';

/**
 * Base class for tree and bush decorations.
 *
 * Decorations are polygon-based map items that render tiled SVG icons within
 * the polygon. They are locked by default to preserve their initial size.
 */
export abstract class PolygonDecorationMapItem extends PolygonMapItem {
    // The generic default square PolygonElement seeds for a new polygon (km units).
    private static readonly GENERIC_DEFAULT = [[0, 0], [0.01, 0], [0.01, 0.01], [0, 0.01]];
    // Upper bound on tiled icons per draw, to keep very large hedges responsive.
    private static readonly MAX_ICONS = 3000;

    private svgImage: HTMLImageElement | null = null;

    constructor(
        formGroup: FormGroup,
        type: string,
        private svgPath: string,
        protected sizeInMeters: number,
        private iconSpacingMeters: number
    ) {
        super(formGroup, type);
        this.setLocked(true);
        this.applySensibleDefaultSize();
        this.loadSvgImage();
    }

    /**
     * Replace PolygonElement's generic default with a sensible one-cell footprint
     * so a new item reads as a single plant that can then be extended.
     */
    private applySensibleDefaultSize(): void {
        const current = this.formGroup.get('PolygonPoints')?.value;
        if (JSON.stringify(current) !== JSON.stringify(PolygonDecorationMapItem.GENERIC_DEFAULT)) {
            return; // a loaded or already-sized polygon — leave it untouched
        }
        const halfKm = (this.iconSpacingMeters / 2) / 1000; // m -> km
        const square = [
            [-halfKm, -halfKm],
            [halfKm, -halfKm],
            [halfKm, halfKm],
            [-halfKm, halfKm]
        ];
        this.formGroup.get('PolygonPoints')?.setValue(square);
        this.points = square.map(p => ({ x: p[0] * 1000, y: p[1] * 1000 })); // km -> m
    }

    private loadSvgImage(): void {
        this.svgImage = new Image();
        this.svgImage.onerror = (e) => console.error('Failed to load decoration SVG:', this.svgPath, e);
        this.svgImage.src = this.svgPath;
    }

    // Use an expanded hit area so small decorations are easy to select.
    public override isPointInside(x: number, y: number): boolean {
        if (super.isPointInside(x, y)) {
            return true;
        }
        const bb = this.getBoundingBox();
        const margin = this.iconSpacingMeters * 0.75;
        return x >= bb.x - margin && x <= bb.x + bb.width + margin &&
               y >= bb.y - margin && y <= bb.y + bb.height + margin;
    }

    public override draw(ctx: CanvasRenderingContext2D, offsetX?: number, offsetY?: number): void {
        // Show the polygon footprint (fill / outline / name) only when selected.
        if (this.selected) {
            super.draw(ctx, offsetX, offsetY);
        }

        const points = this.getPoints();
        if (!this.svgImage || !this.svgImage.complete || points.length === 0) {
            return;
        }

        const ox = offsetX ?? 0;
        const oy = offsetY ?? 0;
        const size = this.sizeInMeters;
        const step = this.iconSpacingMeters;

        // Bounding box (metres).
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const p of points) {
            minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
            maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
        }

        // Default / one-cell footprint: draw a single centred icon.
        if (step <= 0 || (maxX - minX <= step && maxY - minY <= step)) {
            this.drawIcon(ctx, (minX + maxX) / 2 + ox, (minY + maxY) / 2 + oy, size);
            return;
        }

        // Tile icons across the polygon.
        let drawn = 0;
        for (let x = minX + step / 2; x <= maxX && drawn < PolygonDecorationMapItem.MAX_ICONS; x += step) {
            for (let y = minY + step / 2; y <= maxY && drawn < PolygonDecorationMapItem.MAX_ICONS; y += step) {
                if (super.isPointInside(x, y)) {
                    this.drawIcon(ctx, x + ox, y + oy, size);
                    drawn++;
                }
            }
        }

        // Guarantee at least the centre icon for oddly-shaped polygons.
        if (drawn === 0) {
            this.drawIcon(ctx, (minX + maxX) / 2 + ox, (minY + maxY) / 2 + oy, size);
        }
    }

    private drawIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number): void {
        ctx.save();
        ctx.translate(cx, cy);
        try {
            ctx.drawImage(this.svgImage!, -size / 2, -size / 2, size, size);
        } catch (error) {
            console.error('Error drawing decoration icon:', error);
        }
        ctx.restore();
    }
}
